/**
 * 营生系统：打工、委托、证照、地块、雇工。
 *
 * 这一章的主线就是「从翻垃圾到开了自己的店」，所以全部放在一处，方便互相照应：
 *   打工     — 铺子里现成的零活，交东西换钱，顺带攒标签
 *   委托     — 榜上的长活，有期限，交不上要赔
 *   证照     — 办事的流程与阶段推进（今天填表，过几天批）
 *   地块     — 摊位与铺面，租还是买，每月交钱
 *   雇工     — 花钱请人替你干，日结工钱
 */
import { Repo } from '../data/schema.js';
import { Tags } from './tags.js';
import { Avatar } from './avatar.js';
import { getItem } from '../data/items.js';
import { CERT_DEFS } from '../data/tags.js';

/* ============================ 打工 ============================ */

/** 各家铺子能给的零活。need 为空表示只出力。 */
export const JOB_DEFS = [
  { shop: 'garage', id: 'parts', name: '卸废车零件', need: 'scrap_iron', ap: 2, reward: 6, tags: 'labor' },
  { shop: 'garage', id: 'hauling', name: '搬轮胎垛', need: 'bolt', ap: 2, reward: 5, tags: 'labor' },
  { shop: 'eatery', id: 'dishes', name: '后厨洗碗', need: '', ap: 1, reward: 4, tags: 'labor' },
  { shop: 'eatery', id: 'delivery', name: '送外卖', need: '', ap: 2, reward: 7, tags: 'errands' },
  { shop: 'general', id: 'restock', name: '上货', need: 'scrap_wood', ap: 2, reward: 5, tags: 'labor' },
  { shop: 'depot', id: 'loading', name: '装车', need: '', ap: 3, reward: 8, tags: 'hauling' },
  { shop: 'fun_room', id: 'setup', name: '摆桌椅', need: '', ap: 2, reward: 6, tags: 'labor' },
  { shop: 'onsen', id: 'firewood', name: '劈柴烧水', need: 'firewood', ap: 3, reward: 9, tags: 'labor' },
  { shop: 'pet_shop', id: 'mucking', name: '清牲口栏', need: '', ap: 3, reward: 7, tags: 'labor' },
  { shop: 'pharmacy', id: 'herbs', name: '晒药材', need: 'herb', ap: 1, reward: 4, tags: 'labor' },
];

/** 铺子今天的活：同一天内不变，换天才刷新 */
export function refreshJobs(db, scene) {
  const shopId = scene.replace('_hall', '');
  const w = Repo.getWorld(db);
  const table = db.table('jobOffers');
  const pool = JOB_DEFS.filter((j) => j.shop === shopId || (scene === 'garage' && j.shop === 'garage'));
  if (!pool.length) return [];
  const old = table.whereEq('scene', scene).filter((r) => r.created_day === w.day);
  if (old.length) return old;
  const want = Math.min(pool.length, 2 + Math.floor(Math.random() * 2));
  const picked = [];
  const copy = [...pool];
  while (picked.length < want && copy.length) {
    picked.push(copy.splice(Math.floor(Math.random() * copy.length), 1)[0]);
  }
  return picked.map((j) => table.insert({
    scene, type: j.id, name: j.name, needObj: { id: j.need, count: 1 },
    giver: j.shop, reward_gold: j.reward, ap_cost: j.ap, tags: j.tags,
    requires_cert: null, fine_risk: 0, claimCount: 0, created_day: w.day, expires_day: w.day,
  }));
}

/** 接一档活 */
export function claimJob(db, offerId) {
  const offer = db.table('jobOffers').find(offerId);
  if (!offer) return { ok: false, reason: '这活已经没了。' };
  if (offer.claimCount >= 3) return { ok: false, reason: '今天这活给人抢完了。' };
  const need = offer.needObj || {};
  if (offer.requires_cert && !Tags.has(db, offer.requires_cert)) {
    return { ok: false, reason: '人家要看凭据，你没有。' };
  }
  const p = Repo.getPlayer(db);
  if ((p.energy || 0) < offer.ap_cost) return { ok: false, reason: '累得干不动了，先歇歇。' };
  if (need.id) {
    const have = Repo.countItem(db, need.id);
    if (have < need.count) return { ok: false, reason: `还差${getItem(need.id).name}${need.count}个。` };
    Repo.takeItem(db, need.id, need.count);
  }
  db.table('jobOffers').update(offerId, { claimCount: (offer.claimCount || 0) + 1 });
  Repo.savePlayer(db, {
    cash: (p.cash || 0) + offer.reward_gold,
    energy: Math.max(0, (p.energy || 0) - offer.ap_cost),
  });
  Avatar.addFatigue(db, offer.ap_cost * 2);
  Tags.gain(db, offer.tags || 'labor', 1, `job:${offer.type}`, 10);
  Tags.gain(db, 'face', 1, 'job', 12, 2);
  return { ok: true, text: `${offer.name}干完了，得${offer.reward_gold}钱。` };
}

/* ============================ 委托 ============================ */

/** 榜上的长活模板 */
export const QUEST_DEFS = [
  { id: 'hauling', name: '搬三包货', scene: 'depot', item: '', amount: 3, days: 3, reward: 30, gate: 'transportLicense', tag: 'hauling' },
  { id: 'fishRun', name: '送一车活鱼', scene: 'depot', item: 'liveCatfish', amount: 4, days: 3, reward: 45, gate: 'transportLicense', tag: 'hauling' },
  { id: 'herbOrder', name: '药铺要一批草药', scene: 'pharmacy', item: 'herb', amount: 10, days: 4, reward: 35, gate: 'businessTaxClear', tag: 'pharmacy' },
  { id: 'partsRun', name: '车铺急要零件', scene: 'garage', item: 'bolt', amount: 12, days: 2, reward: 28, gate: 'workshopLicense', tag: 'machinery' },
  { id: 'banquetFish', name: '酒楼办席要鲈鱼', scene: 'riverfront', item: 'perch', amount: 3, days: 4, reward: 60, gate: 'businessTaxClear', tag: 'restaurant' },
  { id: 'nightWatch', name: '夜里巡一遭城墙根', scene: 'gate', item: '', amount: 1, days: 1, reward: 18, gate: 'nightPass', tag: 'aid' },
  { id: 'cleanYard', name: '给衙前广场清垃圾', scene: 'yamen_sq', item: '', amount: 2, days: 2, reward: 22, gate: 'citizenRights', tag: 'labor' },
];

/** 榜上的活按天生成，同一场景同一任务不重复 */
export function refreshQuests(db, scene) {
  const w = Repo.getWorld(db);
  const table = db.table('questOffers');
  const has = table.whereEq('scene', scene).some((r) => r.spawn_day === w.day);
  if (has) return table.whereEq('scene', scene).filter((r) => r.spawn_day === w.day);
  const pool = QUEST_DEFS.filter((q) => q.scene === scene);
  return pool.map((q) => table.insert({
    scene, type: q.id, name: q.name, dedupeKey: `${scene}:${q.id}`, spawn_day: w.day,
    target: { id: q.item, amount: q.amount }, amount_required: q.amount,
    time_limit_minutes: 0, has_deadline_days: q.days, reward_gold: q.reward,
    gate_cert: q.gate, tag: q.tag,
  }));
}

/** 报名一个委托 */
export function acceptQuest(db, offerId) {
  const offer = db.table('questOffers').find(offerId);
  if (!offer) return { ok: false, reason: '这榜被人揭走了。' };
  if (offer.gate_cert && !Tags.has(db, offer.gate_cert)) {
    return { ok: false, reason: `人家要先看${(CERT_DEFS[offer.gate_cert] || {}).name || offer.gate_cert}。` };
  }
  const mine = db.table('playerQuests').all().filter((q) => q.status === 'active');
  if (mine.length >= 3) return { ok: false, reason: '手里压着三档活了，先干完再接。' };
  const w = Repo.getWorld(db);
  const row = db.table('playerQuests').insert({
    offerId, scene: offer.scene, type: offer.type, name: offer.name,
    target: offer.target, required: offer.amount_required, progress: 0,
    deadline: w.day + (offer.has_deadline_days || 3), time_limit_minutes: 0,
    has_deadline_days: offer.has_deadline_days, reward_gold: offer.reward_gold,
    tag: offer.tag, status: 'active',
  });
  db.table('questOffers').remove(offerId);
  return { ok: true, text: `接下了「${offer.name}」，${offer.has_deadline_days}天内交。`, row };
}

/** 交委托：够数就能交 */
export function redeemQuest(db, questId) {
  const q = db.table('playerQuests').find(questId);
  if (!q) return { ok: false, reason: '没有这档活。' };
  if (q.status !== 'active') return { ok: false, reason: '这档活已经了结了。' };
  const target = q.target || {};
  if (target.id) {
    const have = Repo.countItem(db, target.id);
    if (have < q.required) return { ok: false, reason: `还差${getItem(target.id).name}${q.required - have}个。` };
    Repo.takeItem(db, target.id, q.required);
  }
  const p = Repo.getPlayer(db);
  Repo.savePlayer(db, { cash: (p.cash || 0) + q.reward_gold });
  db.table('playerQuests').update(questId, { status: 'redeemed' });
  Tags.gain(db, q.tag || 'aid', 2, `quest:${q.type}`, 10);
  Tags.gain(db, 'face', 2, 'quest', 12);
  // 断链补上：交差换来街坊的信任，也说明你这人实诚、肯下力
  Tags.gain(db, 'trustOfLocals', 1, 'quest', 12);
  Tags.gain(db, 'honest', 1, 'quest', 12);
  Tags.gain(db, 'industrious', 1, 'quest', 12);
  return { ok: true, text: `「${q.name}」交了差，得${q.reward_gold}钱。` };
}

/** 每一天推进：超期的活算失败 */
export function tickQuests(db) {
  const w = Repo.getWorld(db);
  let failed = 0;
  for (const q of db.table('playerQuests').all()) {
    if (q.status !== 'active') continue;
    if (q.deadline && w.day > q.deadline) {
      db.table('playerQuests').update(q.id, { status: 'failed' });
      Tags.gain(db, 'face', -3, 'quest_fail', 12);
      failed += 1;
    }
  }
  return failed;
}

export function activeQuests(db) {
  return db.table('playerQuests').all().filter((q) => q.status === 'active');
}

/* ============================ 证照 ============================ */

/** 一张证照怎么办：各阶段要几日、多少钱 */
export const LICENSE_STEPS = [
  { stage: 1, name: '递状', wait: 0, fee: 0 },
  { stage: 2, name: '核查', wait: 2, fee: 5 },
  { stage: 3, name: '具保', wait: 2, fee: 10 },
  { stage: 4, name: '给照', wait: 1, fee: 15 },
];

export function applyLicense(db, certId) {
  if (!CERT_DEFS[certId]) return { ok: false, reason: '没有这张照。' };
  const table = db.table('licenseAudits');
  if (table.all().some((r) => r.needId === certId && r.active)) return { ok: false, reason: '这一张已经在办了。' };
  const have = db.table('licenses').all().some((r) => r.needId === certId && r.status === 'active');
  if (have) return { ok: false, reason: '这张照你手上有。' };
  const p = Repo.getPlayer(db);
  const step = LICENSE_STEPS[0];
  if ((p.cash || 0) < step.fee) return { ok: false, reason: `递状要${step.fee}钱。` };
  Repo.savePlayer(db, { cash: (p.cash || 0) - step.fee });
  const w = Repo.getWorld(db);
  const row = table.insert({ needId: certId, kind: 'new', type: 'license', stage: 1, stage_started_day: w.day, active: true });
  return { ok: true, text: `${(CERT_DEFS[certId] || {}).name}递上去了，等核查。`, row };
}

/** 每天看看办到哪一步了 */
export function tickLicenses(db) {
  const w = Repo.getWorld(db);
  const out = [];
  for (const rec of db.table('licenseAudits').all()) {
    if (!rec.active) continue;
    const step = LICENSE_STEPS[rec.stage - 1];
    if (!step) continue;
    if (w.day - (rec.stage_started_day || w.day) < step.wait) continue;
    if (rec.stage >= LICENSE_STEPS.length) {
      db.table('licenses').insert({
        needId: rec.needId, status: 'active', issueDay: w.day, expiryDay: w.day + 180,
        max_allowed: Tags.get(db, rec.needId).total + 10,
      });
      Tags.grantCert(db, rec.needId, 'license');
      Tags.gain(db, rec.needId, 10, 'license', 20);
      db.table('licenseAudits').update(rec.id, { active: false, stage: LICENSE_STEPS.length });
      out.push(rec.needId);
      continue;
    }
    const next = LICENSE_STEPS[rec.stage];
    const p = Repo.getPlayer(db);
    if ((p.cash || 0) < next.fee) {
      out.push({ stalled: rec.needId, fee: next.fee });
      continue;
    }
    Repo.savePlayer(db, { cash: (p.cash || 0) - next.fee });
    db.table('licenseAudits').update(rec.id, { stage: rec.stage + 1, stage_started_day: w.day });
    out.push({ stage: rec.stage + 1, needId: rec.needId });
  }
  return out;
}

export function myLicenses(db) {
  return db.table('licenses').all().filter((r) => r.status === 'active');
}

export function pendingLicenses(db) {
  return db.table('licenseAudits').all().filter((r) => r.active);
}

/* ============================ 地块与摊位 ============================ */

/** 铺面：按场景定死的几块地，够用了 */
export const UNIT_DEFS = [
  { id: 'stall_market', scene: 'market', name: '市集摊位', rentPerMonth: 30, price: 400, level: 1 },
  { id: 'stall_main', scene: 'main_street', name: '主街摊位', rentPerMonth: 60, price: 900, level: 2 },
  { id: 'shop_fun', scene: 'fun_street', name: '瓦舍街铺面', rentPerMonth: 90, price: 1400, level: 3 },
  { id: 'shop_main', scene: 'main_street', name: '主街铺面', rentPerMonth: 120, price: 2000, level: 4 },
];

export function unitsAt(db, scene) {
  const table = db.table('shopUnits');
  for (const def of UNIT_DEFS.filter((u) => u.scene === scene)) {
    if (!table.find(def.id)) {
      // last_paid_period 从 -1 起：第 0 期（前 30 天）还没交过租，不然开局就交不上
      table.insert({ ...def, forSale: true, owned: false, for_rent: true, last_paid_period: -1 });
    }
  }
  return table.whereEq('scene', scene);
}

export function myUnits(db) {
  return db.table('shopUnits').all().filter((u) => u.owned);
}

export function buyUnit(db, unitId) {
  const unit = db.table('shopUnits').find(unitId);
  if (!unit) return { ok: false, reason: '没有这地块。' };
  if (unit.owned) return { ok: false, reason: '已经是你的了。' };
  const p = Repo.getPlayer(db);
  if ((p.cash || 0) < unit.price) return { ok: false, reason: `买这块地要${unit.price}钱。` };
  Repo.savePlayer(db, { cash: (p.cash || 0) - unit.price });
  db.table('shopUnits').update(unitId, { owned: true, forSale: false, for_rent: false });
  Tags.grantCert(db, 'landDeed', 'land');
  Tags.gain(db, 'landDeed', 20, `land:${unitId}`, 40);
  return { ok: true, text: `${unit.name}买下来了，地契在你的名下。` };
}

export function rentUnit(db, unitId) {
  const unit = db.table('shopUnits').find(unitId);
  if (!unit) return { ok: false, reason: '没有这地块。' };
  if (unit.owned) return { ok: false, reason: '已经是你的了。' };
  const w = Repo.getWorld(db);
  const period = Math.floor(w.day / 30);
  if (unit.last_paid_period === period) return { ok: false, reason: '这个月的租已经交过了。' };
  const p = Repo.getPlayer(db);
  if ((p.cash || 0) < unit.rentPerMonth) return { ok: false, reason: `一个月${unit.rentPerMonth}钱。` };
  Repo.savePlayer(db, { cash: (p.cash || 0) - unit.rentPerMonth });
  db.table('shopUnits').update(unitId, { last_paid_period: period });
  Tags.gain(db, 'landDeed', 2, `rent:${unitId}`, 20);
  return { ok: true, text: `${unit.name}这个月归你摆货。` };
}

/* ============================ 雇工 ============================ */

const STAFF_NAMES = ['小六', '阿黄', '栓子', '二狗', '春生', '老根', '顺子', '有福'];

export function hirePool(db) {
  const w = Repo.getWorld(db);
  const table = db.table('hired_staff');
  if (table.all().length === 0) {
    for (let i = 0; i < 3; i += 1) {
      const name = STAFF_NAMES[(w.day * 3 + i) % STAFF_NAMES.length];
      table.insert({
        display_name: `${name}${i ? '·' + i : ''}`, hire_cost_gold: 40 + i * 15,
        wage_per_day_gold: 6 + i * 3, assigned_scene: '', task_id: '', energy: 100,
        gender: i % 2 ? '女' : '男',
      });
    }
  }
  return table.all().filter((s) => !s.assigned_scene);
}

export function hireStaff(db, staffId) {
  const staff = db.table('hired_staff').find(staffId);
  if (!staff) return { ok: false, reason: '这人已经走了。' };
  const p = Repo.getPlayer(db);
  if ((p.cash || 0) < staff.hire_cost_gold) return { ok: false, reason: `请他要${staff.hire_cost_gold}钱。` };
  Repo.savePlayer(db, { cash: (p.cash || 0) - staff.hire_cost_gold });
  const scene = myUnits(db).map((u) => u.scene)[0] || (p.scene || '');
  db.table('hired_staff').update(staffId, { assigned_scene: scene });
  return { ok: true, text: `${staff.display_name}跟你干了，日工钱${staff.wage_per_day_gold}。` };
}

export function myStaff(db) {
  return db.table('hired_staff').all().filter((s) => s.assigned_scene);
}

/** 派活：交给雇工去跑一趟委托（简单起见只推进度） */
export function assignStaff(db, staffId, questId) {
  const staff = db.table('hired_staff').find(staffId);
  const quest = db.table('playerQuests').find(questId);
  if (!staff || !quest) return { ok: false, reason: '没有这个人，或没有这档活。' };
  db.table('hired_staff').update(staffId, { task_id: questId });
  const amount = Math.max(1, Math.ceil(quest.required / 3));
  const next = Math.min(quest.required, (quest.progress || 0) + amount);
  db.table('playerQuests').update(questId, { progress: next });
  return { ok: true, text: `${staff.display_name}去了，${quest.name}进度${next}/${quest.required}。` };
}

/* ============================ 每日结算 ============================ */

/** 天亮时统一过一遍：超期的委托、办到哪一步的证照、雇工的工钱 */
export function daily(db) {
  const msgs = [];
  const failed = tickQuests(db);
  if (failed) msgs.push(`${failed}档活没交上，街坊都知道了。`);

  for (const rec of tickLicenses(db)) {
    if (typeof rec === 'string') msgs.push(`${(CERT_DEFS[rec] || {}).name}批下来了。`);
    else if (rec && rec.stalled) msgs.push(`${(CERT_DEFS[rec.stalled] || {}).name}卡着了，还差${rec.fee}钱。`);
  }

  const staff = myStaff(db);
  if (staff.length) {
    const wage = staff.reduce((s, x) => s + (x.wage_per_day_gold || 0), 0);
    const p = Repo.getPlayer(db);
    Repo.savePlayer(db, { cash: Math.max(0, (p.cash || 0) - wage) });
    msgs.push(`发给${staff.length}个伙计${wage}钱工钱。`);
  }

  // 摊位与铺面：名下每处一日有一点赞助（原先只出不进，如今也有了回头钱）
  const period = Math.floor((Repo.getWorld(db).day || 1) / 30);
  let earn = 0;
  for (const u of db.table('shopUnits').all()) {
    if (u.owned) earn += 4 + (u.level || 1) * 2;
    else if (u.last_paid_period === period) earn += 2 + (u.level || 1);
  }
  if (earn > 0) {
    const p = Repo.getPlayer(db);
    Repo.savePlayer(db, { cash: (p.cash || 0) + earn });
    msgs.push(`摊上今日进了${earn}钱。`);
  }

  Avatar.daily(db);
  return msgs;
}

export const Jobs = {
  refreshJobs, claimJob,
  refreshQuests, acceptQuest, redeemQuest, activeQuests,
  applyLicense, tickLicenses, myLicenses, pendingLicenses,
  unitsAt, myUnits, buyUnit, rentUnit,
  hirePool, hireStaff, myStaff, assignStaff,
  daily,
};
